import { apiService } from '../services/ApiService';
import { connectionService } from '../services/ConnectionService';
import Store from '../wrappers/Store';
export const SERVER_PDF_ERROR = {
    offline: 'offline',
    timeout: 'timeout',
    rateLimited: 'rate_limited',
    documentTooLarge: 'document_too_large',
    saveFailed: 'save_failed',
    general: 'general',
};
/**
 * Longer than the server's whole budget (renderer 30 s, then the mail): a
 * request that gets no response is read as "offline" by the API client, so a
 * slow render must never time out on this side first.
 */
const REQUEST_TIMEOUT_MS = 120000;
/** The print stylesheet every app serves next to itself (the kit's compiled print.css). */
const STYLESHEET_URL = '/print/print.css';
/**
 * Upload ceiling, below the ingress's default 1 MB body limit. A body the
 * ingress refuses comes back without CORS headers, which the browser reports
 * as a network error — the API client would then mark the app offline and
 * the user would read "timed out". Gzipped, this ceiling is far beyond the
 * server's own document cap; it bites where the platform cannot compress.
 */
const MAX_UPLOAD_BYTES = 900 * 1024;
/**
 * Renders a printable to PDF on the server (a headless Chromium behind
 * aria-api) — real text, embedded fonts — and returns it or has it mailed.
 * The app sends exactly what it prints: the printable's HTML and the print
 * stylesheet; the server owns the page around them (fonts, page size,
 * margins, layout, page numbers). Online only: the server is the renderer.
 *
 * A document kind is offered only while the server lists it in
 * `appConfig.pdf.kinds` (/initialize).
 */
class ServerPdfService {
    stylesheet = null;
    supports(kind) {
        return !!Store.get('appConfig')?.pdf?.kinds?.includes(kind);
    }
    /** POS routes sit at the API root, the backoffice twins under endpointBase '/admin'. */
    endpointFor(kind, email, endpointBase = '') {
        return `${endpointBase}/pdf/${kind}${email ? '/email' : ''}`;
    }
    render(printable, document, endpointBase = '') {
        return this.post(printable, document, null, endpointBase);
    }
    email(printable, document, to, endpointBase = '') {
        return this.post(printable, document, to, endpointBase);
    }
    async post(printable, document, to, endpointBase) {
        if (!connectionService.isOnline) {
            return { success: false, error: SERVER_PDF_ERROR.offline };
        }
        let body;
        try {
            body = await this.formData(printable, document, to);
        }
        catch (error) {
            console.error('Server PDF: preparing the document failed', error);
            return { success: false, error: SERVER_PDF_ERROR.general };
        }
        if (this.uploadBytes(body) > MAX_UPLOAD_BYTES) {
            return { success: false, error: SERVER_PDF_ERROR.documentTooLarge };
        }
        try {
            const response = await apiService.post(this.endpointFor(document.kind, to !== null, endpointBase), body, { timeout: REQUEST_TIMEOUT_MS });
            return { success: true, pdf: response.data?.pdf, bytes: response.data?.bytes, client: response.data?.client };
        }
        catch (error) {
            return { success: false, error: this.errorCode(error) };
        }
    }
    async formData(printable, document, to) {
        const html = (await printable.toHtml({ wrapperClass: document.wrapperClass, forceLightTheme: true })).forPrint();
        const form = new FormData();
        form.append('document', await this.part(html, 'text/html'), 'document.html');
        form.append('stylesheet', await this.part(await this.loadStylesheet(), 'text/css'), 'document.css');
        form.append('profile', document.profile);
        form.append('title', document.title);
        form.append('period', document.period);
        form.append('file_name', document.fileName);
        form.append('page_label', document.pageLabel);
        form.append('lang', document.lang);
        if (to !== null) {
            form.append('to', to);
            for (const [name, value] of Object.entries(document.fields ?? {})) {
                form.append(name, value);
            }
        }
        return form;
    }
    uploadBytes(form) {
        let bytes = 0;
        form.forEach(value => { bytes += typeof value === 'string' ? value.length : value.size; });
        return bytes;
    }
    /**
     * A file part, gzipped where the platform can (CompressionStream: Chromium
     * 80+, Safari 16.4+) — report HTML is a few percent of its size compressed,
     * which keeps long reports far below ingress body limits. The server
     * recognises gzip by its magic bytes; a platform without the API sends the
     * text as it is.
     */
    async part(text, type) {
        const plain = new Blob([text], { type });
        if (typeof CompressionStream === 'undefined') {
            return plain;
        }
        try {
            return await new Response(plain.stream().pipeThrough(new CompressionStream('gzip'))).blob();
        }
        catch {
            return plain;
        }
    }
    loadStylesheet() {
        if (!this.stylesheet) {
            this.stylesheet = fetch(STYLESHEET_URL)
                .then(response => {
                if (!response.ok)
                    throw new Error(`print stylesheet answered ${response.status}`);
                return response.text();
            })
                .catch(error => {
                this.stylesheet = null;
                throw error;
            });
        }
        return this.stylesheet;
    }
    /**
     * Server rejections carry errors[].code. 413 comes from the ingress, 429
     * from the rate limiter, both without a code. No response at all — aborted
     * or timed out — means an email may still have gone out: "delivery unknown".
     */
    errorCode(error) {
        const code = error?.response?.data?.errors?.find((e) => !!e?.code)?.code;
        if (code)
            return code;
        const status = error?.response?.status;
        if (status === 413)
            return SERVER_PDF_ERROR.documentTooLarge;
        if (status === 429)
            return SERVER_PDF_ERROR.rateLimited;
        if (!error?.response || error?.code === 'ECONNABORTED' || /timeout|network error/i.test(error?.message || '')) {
            return SERVER_PDF_ERROR.timeout;
        }
        return SERVER_PDF_ERROR.general;
    }
}
export const serverPdf = new ServerPdfService();
//# sourceMappingURL=ServerPdfService.js.map